import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemOut, ReviewStatus } from '@/api/research';
import { Button, Drawer, Input, Segmented, Textarea, useToast } from '@/design-system';
import { formatDateShort, formatTime } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { patchItem, versionsQuery } from './api';
import s from './research.module.css';

/**
 * One research item (§9 C9, additive): edit the name, category, price and description, set its review
 * status, see its versions, delete it. PATCH /admin/research-items/{id} makes a new version.
 */
export function EditItemDrawer({ item, onClose, onSaved, onDelete }: { item: ItemOut; onClose: () => void; onSaved: (next: ItemOut) => void; onDelete: () => void }) {
  const { t } = useTranslation('research');
  const { t: tc } = useTranslation('common');
  const tv = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const toast = useToast();
  const str = (k: string) => (item.data[k] === null || item.data[k] === undefined ? '' : String(item.data[k]));
  const [name, setName] = useState(str('name'));
  const [category, setCategory] = useState(str('category'));
  const [price, setPrice] = useState(str('price'));
  const [description, setDescription] = useState(str('description'));
  const [status, setStatus] = useState<ReviewStatus>(item.review_status);
  const [problem, setProblem] = useState<string | null>(null);
  const versions = useQuery(versionsQuery(item.id)).data ?? [];

  const save = useMutation({
    mutationFn: () => {
      const next: Record<string, unknown> = {};
      const blank = (v: string) => v.trim() || null;
      if (name.trim() !== str('name')) next.name = name.trim();
      if (category.trim() !== str('category')) next.category = blank(category);
      const p = price.replace(/\s/g, '');
      if (p !== str('price')) next.price = p || null;
      if (description.trim() !== str('description')) next.description = blank(description);
      return patchItem(item.id, { ...(Object.keys(next).length ? { data: next } : {}), ...(status !== item.review_status ? { review_status: status } : {}) });
    },
    onSuccess: (next) => {
      toast(t('items.saved', { version: next.version }));
      onSaved(next);
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    save.mutate();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('items.edit_title')}
      sub={t('items.edit_sub')}
      closeLabel={tc('actions.close_esc')}
      footerNote={
        problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )
      }
      footer={
        <div className={s.drawerActions}>
          <Button size={38} variant="danger" icon="trash-2" iconSize={14} onClick={onDelete}>
            {t('items.delete')}
          </Button>
          <Button size={38} type="submit" form="research-item" disabled={save.isPending}>
            {t('items.save')}
          </Button>
        </div>
      }
    >
      <form id="research-item" className={s.drawerForm} onSubmit={submit}>
        <Input label={t('items.name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <div className={s.grid2}>
          <Input label={t('items.category')} value={category} onChange={(e) => setCategory(e.target.value)} />
          <Input label={t('items.price')} inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
        <Textarea label={t('items.description')} rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className={s.group}>
          <span className={s.groupLabel}>{t('items.review')}</span>
          <Segmented
            variant="form"
            label={t('items.review')}
            value={status}
            onChange={setStatus}
            className={s.alignStart}
            options={(['new', 'approved', 'rejected'] as const).map((k) => ({ value: k, label: t(`items.status.${k}`) }))}
          />
        </div>
        {/* Facts read from the item's photos (P8-X02): what the image shows, unverified until the PM saves. */}
        {!!item.image_facts?.length && (
          <div className={s.group}>
            <span className={s.groupLabel}>{t('items.image_facts')}</span>
            {item.image_facts.map((f, i) => (
              <div key={`${f.asset}-${i}`} className={s.versionRow}>
                <span>{f.field === 'price' ? t('items.image_fact_price', { text: f.text }) : t('items.image_fact_sizes', { value: f.value })}</span>
                <span className={s.versionWhen}>{f.applied ? t('items.from_image') : t('items.image_fact_kept')}</span>
              </div>
            ))}
          </div>
        )}
        {item.source_url && (
          <a className={s.sourceLink} href={item.source_url} target="_blank" rel="noreferrer noopener">
            {t('items.source_link')}: {item.source_url.replace(/^https?:\/\//, '')}
          </a>
        )}
        {versions.length > 0 && (
          <div className={s.group}>
            <span className={s.groupLabel}>{t('items.versions')}</span>
            {versions.map((v) => (
              <div key={v.version} className={s.versionRow}>
                <span>{t('items.version_row', { version: v.version, change: tv(`items.change.${v.change}`, { defaultValue: v.change }) })}</span>
                <span className={s.versionWhen}>{`${formatDateShort(v.created_at)} ${formatTime(v.created_at)}`}</span>
              </div>
            ))}
          </div>
        )}
      </form>
    </Drawer>
  );
}
